import React, { useState } from 'react';
import { AnimatedSection } from '../../../lib/animations/AnimatedSection';
import { Check, X, Loader2, Heart, Minus, Plus, Send, Info, UtensilsCrossed } from 'lucide-react';
import axios from 'axios';
import RSVPBackgroundLayer from './RSVPBackgroundLayer';
import PreviewModeNotice from './PreviewModeNotice';
import { getRsvpButtonShadowStyle } from '../../../lib/utils/buttonShadow';
import { resolveRsvpLabels } from '../../../lib/utils/rsvpLabels';
import {
  isBackgroundLayerActive,
  isSurfaceVisible,
  getRsvpBackgroundLayerStyle,
  getRsvpSurfaceStyle,
  getBgLayerWidthPercent,
  getSurfaceWidthPercent,
  getSurfaceAlpha,
  getBgLayerAlpha,
  getContentWidthStyle,
  getBgLayerRadiusPx,
  getSurfaceRadiusPx,
} from '../../../lib/utils/responseCardStyle';

const API = process.env.REACT_APP_BACKEND_URL;

const DIETARY_OPTIONS = ['Vegan', 'Vegetarian', 'Gluten-free', 'Lactose-free'];

export default function RsvpDetailed({ section, data, theme, settings, rsvpRef }) {
  const anim = section.animation || {};
  const cs = data.customStyle || {};
  const labels = resolveRsvpLabels(data.labels);
  const bg = data.background;
  const motionPreset = settings?.motionPreset || 'none';
  const disableAnim = motionPreset !== 'none';

  const accentColor = cs.accentColor || theme.accent;
  const textColor = cs.textColor || theme.textPrimary;
  const secondaryColor = theme.textSecondary;
  const bgColor = cs.bgColor || theme.surface || theme.bg;
  const headingFont = cs.fontFamily || theme.headingFont;
  const bodyFont = theme.bodyFont;
  const eyebrowFont = theme.eyebrowFont;
  const buttonBg = cs.buttonColor || theme.buttonBg || accentColor;
  const buttonText = cs.buttonTextColor || theme.buttonText || '#ffffff';

  // Phase RSVP Style 3.0 — three real layers. No inner form-body card.
  const bgActive = isBackgroundLayerActive(cs);
  const surfaceVisible = isSurfaceVisible(cs);
  const bgWidthPct = getBgLayerWidthPercent(cs);
  const surfaceWidthPct = getSurfaceWidthPercent(cs);
  const surfaceAlpha = getSurfaceAlpha(cs);
  // Section-level artwork opacity tied to Background opacity when bgActive.
  const sectionImageAlpha = bgActive ? getBgLayerAlpha(cs) : 1;
  const contentWidthStyle = getContentWidthStyle(cs);

  // Background layer style (outer block, opt-in via bgLayerType='color').
  // Width: 100% = section content width. We strip section horizontal padding
  // when active so bgWidthPercent=100 reaches the viewport edges.
  const bgWrapperStyle = bgActive ? {
    width: `${bgWidthPct}%`,
    borderRadius: getBgLayerRadiusPx(cs) !== null ? `${getBgLayerRadiusPx(cs)}px` : '1.25rem',
    overflow: 'hidden',
    ...getRsvpBackgroundLayerStyle(cs, { backgroundColor: bgColor, borderColor: undefined }),
  } : null;

  // Surface layer style (inner card material — the ONE place material lives).
  // Width semantics: when bgActive → % of bg-layer parent; when inactive → variant max-w-md.
  const surfaceWidthStyle = bgActive
    ? { width: `${surfaceWidthPct}%`, maxWidth: '100%' }
    : (surfaceWidthPct < 100 ? { width: `${surfaceWidthPct}%`, maxWidth: '100%' } : null);
  const surfaceMaterial = surfaceVisible
    ? getRsvpSurfaceStyle(cs, { backgroundColor: bgColor, borderColor: `${textColor}1f` })
    : {};
  const surfaceLayerStyle = {
    ...(surfaceWidthStyle || {}),
    ...(bgActive ? {} : { maxWidth: '28rem' }),
    ...(surfaceVisible ? {
      ...surfaceMaterial,
      borderRadius: getSurfaceRadiusPx(cs) !== null ? `${getSurfaceRadiusPx(cs)}px` : '1.25rem',
      overflow: 'hidden',
      border: surfaceMaterial.borderColor ? '1px solid' : undefined,
    } : { background: 'transparent' }),
  };

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    attending: null,
    guest_count: 1,
    dietary_preferences: [],
    allergies: '',
    children_coming: null,
    children_count: 0,
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.attending === null || !form.name.trim()) return;
    // Phase 2.2 Lite — review mode: keep the form visible but inert.
    if (settings?.disableRsvpSubmit) {
      setError('Review mode — RSVP submission is disabled on the preview link.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/public/invite/${settings.slug}/rsvp`, {
        name: form.name,
        email: form.email || undefined,
        phone: form.phone || undefined,
        attending: form.attending,
        guest_count: form.guest_count,
        dietary_preferences: form.dietary_preferences.length ? form.dietary_preferences : undefined,
        allergies: form.allergies || undefined,
        children_coming: form.children_coming,
        children_count: form.children_count,
        message: form.message || undefined,
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleDietary = (opt) => {
    setForm(prev => ({
      ...prev,
      dietary_preferences: prev.dietary_preferences.includes(opt)
        ? prev.dietary_preferences.filter(d => d !== opt)
        : [...prev.dietary_preferences, opt]
    }));
  };

  // ─── 3-Layer Renderer ──────────────────────────────────────
  // <section .rsvp-section>
  //   <BackgroundLayer target="section" />  (legacy image/texture)
  //   <div .rsvp-background-layer> | direct
  //     <div .rsvp-surface-layer (material)>
  //       <BackgroundLayer target="card" />
  //       <div .rsvp-form-layer>{formContent}</div>
  //     </div>
  //   </div>
  // </section>
  const renderTree = (formContent) => {
    const surfaceJsx = (
      <div
        className="mx-auto relative rsvp-bg-host"
        style={surfaceLayerStyle}
        data-testid="rsvp-surface-layer"
      >
        {surfaceVisible && (
          <RSVPBackgroundLayer background={bg} target="card" surfaceAlpha={surfaceAlpha} />
        )}
        <div
          className="relative z-10 mx-auto p-6 sm:p-8 rsvp-bg-content"
          style={contentWidthStyle || undefined}
          data-testid="rsvp-form-layer"
        >
          {formContent}
        </div>
      </div>
    );
    return (
      <section
        ref={rsvpRef}
        className={`${bgActive ? 'py-20' : 'px-5 py-20'} rsvp-bg-host`}
        style={{ background: 'transparent' }}
        data-testid="rsvp-section"
      >
        <RSVPBackgroundLayer background={bg} target="section" surfaceAlpha={sectionImageAlpha} />
        {bgActive ? (
          <div className="mx-auto relative rsvp-bg-host" style={bgWrapperStyle} data-testid="rsvp-background-layer">
            <div className="p-4 sm:p-6">{surfaceJsx}</div>
          </div>
        ) : surfaceJsx}
      </section>
    );
  };

  if (success) {
    return renderTree(
      <AnimatedSection preset="fade-up" disableAnimation={disableAnim}>
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ backgroundColor: `${accentColor}20` }}>
            <Heart className="w-10 h-10" style={{ color: accentColor }} />
          </div>
          <h3 className="text-2xl font-semibold mb-2" style={{ color: textColor, fontFamily: headingFont }}>
            {labels.thankYouTitle || 'Thank You!'}
          </h3>
          <p style={{ color: secondaryColor, fontFamily: bodyFont }}>
            {form.attending
              ? (labels.thankYouAccept || "We're so excited to celebrate with you!")
              : (labels.thankYouDecline || "We'll miss you, but thank you for letting us know.")}
          </p>
        </div>
      </AnimatedSection>
    );
  }

  const renderFormBody = () => (<>
        <PreviewModeNotice visible={!!settings?.disableRsvpSubmit} />
        <AnimatedSection preset={anim.section || 'fade-up'} disableAnimation={disableAnim}>
          <p
            className="text-xs tracking-[0.3em] uppercase mb-3 text-center"
            style={{ color: accentColor, fontFamily: eyebrowFont }}
          >
            {data.title || 'RSVP'}
          </p>
          <h3 className="text-xl text-center mb-2" style={{ color: textColor, fontFamily: headingFont }}>
            {labels.heading || 'Will you be joining us?'}
          </h3>
          <p className="text-center text-sm mb-2" style={{ color: secondaryColor, fontFamily: bodyFont }}>
            {data.subtitle || "We'd love to celebrate with you"}
          </p>
          {data.deadline && (
            <p className="text-xs tracking-[0.2em] uppercase text-center mb-8" style={{ color: secondaryColor, fontFamily: eyebrowFont }}>
              {data.deadline}
            </p>
          )}
        </AnimatedSection>

        <AnimatedSection preset={anim.section || 'fade-up'} delay={0.1} disableAnimation={disableAnim}>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-5">

              {/* Full Name */}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: textColor, fontFamily: eyebrowFont }}>
                  {labels.nameLabel || 'Full Name'} *
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={labels.namePlaceholder || 'Your name'}
                  required
                  className="w-full px-4 py-3 rounded-xl border text-sm outline-none transition-colors focus:ring-2 focus:ring-offset-1"
                  style={{ borderColor: `${textColor}20`, color: textColor, background: 'transparent', fontFamily: bodyFont, '--tw-ring-color': accentColor }}
                  data-testid="rsvp-name-input"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: textColor, fontFamily: eyebrowFont }}>
                  {labels.emailLabel || 'Email (Optional)'}
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder={labels.emailPlaceholder || 'your@email.com'}
                  className="w-full px-4 py-3 rounded-xl border text-sm outline-none transition-colors focus:ring-2 focus:ring-offset-1"
                  style={{ borderColor: `${textColor}20`, color: textColor, background: 'transparent', fontFamily: bodyFont, '--tw-ring-color': accentColor }}
                  data-testid="rsvp-email-input"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: textColor, fontFamily: eyebrowFont }}>
                  {labels.phoneLabel || 'Phone (Optional)'}
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={e => setForm(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder={labels.phonePlaceholder || '+1 (555) 000-0000'}
                  className="w-full px-4 py-3 rounded-xl border text-sm outline-none transition-colors focus:ring-2 focus:ring-offset-1"
                  style={{ borderColor: `${textColor}20`, color: textColor, background: 'transparent', fontFamily: bodyFont, '--tw-ring-color': accentColor }}
                  data-testid="rsvp-phone-input"
                />
              </div>

              {/* Attend Yes/No */}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: textColor, fontFamily: eyebrowFont }}>
                  {labels.attendLabel || 'Will you attend?'} *
                </label>
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, attending: true }))}
                    className="w-full py-3.5 rounded-xl border-2 text-sm font-medium transition-all"
                    style={form.attending === true ? {
                      borderColor: buttonBg,
                      backgroundColor: buttonBg,
                      color: buttonText,
                      fontFamily: bodyFont,
                    } : {
                      borderColor: `${textColor}20`,
                      color: textColor,
                      background: 'transparent',
                      fontFamily: bodyFont,
                    }}
                    data-testid="rsvp-attend-yes"
                  >
                    {labels.yesLabel || "Yes, I'll be there!"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, attending: false }))}
                    className="w-full py-3.5 rounded-xl border-2 text-sm font-medium transition-all"
                    style={form.attending === false ? {
                      borderColor: `${textColor}40`,
                      backgroundColor: `${textColor}10`,
                      color: textColor,
                      fontFamily: bodyFont,
                    } : {
                      borderColor: `${textColor}20`,
                      color: textColor,
                      background: 'transparent',
                      fontFamily: bodyFont,
                    }}
                    data-testid="rsvp-attend-no"
                  >
                    {labels.noLabel || "No, I can't make it"}
                  </button>
                </div>
              </div>

              {/* Guest Count — only if attending */}
              {form.attending && (
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: textColor, fontFamily: eyebrowFont }}>
                    {labels.guestCountLabel || 'How many guests in total?'}
                  </label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setForm(prev => ({ ...prev, guest_count: Math.max(1, prev.guest_count - 1) }))}
                      className="w-10 h-10 rounded-full border-2 flex items-center justify-center transition-colors hover:bg-gray-50"
                      style={{ borderColor: `${textColor}20`, color: textColor }}
                      data-testid="rsvp-guest-minus"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="text-xl font-semibold w-10 text-center" style={{ color: textColor, fontFamily: headingFont }}>
                      {form.guest_count}
                    </span>
                    <button
                      type="button"
                      onClick={() => setForm(prev => ({ ...prev, guest_count: Math.min(10, prev.guest_count + 1) }))}
                      className="w-10 h-10 rounded-full border-2 flex items-center justify-center transition-colors hover:bg-gray-50"
                      style={{ borderColor: `${textColor}20`, color: textColor }}
                      data-testid="rsvp-guest-plus"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Menu Preferences — only if attending */}
              {form.attending && (
                <div className="rounded-xl p-4" style={{ background: `${textColor}05` }}>
                  <div className="flex items-center gap-2 mb-3">
                    <UtensilsCrossed className="w-4 h-4" style={{ color: accentColor }} />
                    <label className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: textColor, fontFamily: eyebrowFont }}>
                      {labels.dietaryLabel || 'Menu Preference'}
                    </label>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {DIETARY_OPTIONS.map(opt => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => toggleDietary(opt)}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all text-left"
                        style={form.dietary_preferences.includes(opt) ? {
                          borderColor: accentColor,
                          backgroundColor: `${accentColor}15`,
                          color: textColor,
                          fontFamily: bodyFont,
                        } : {
                          borderColor: `${textColor}15`,
                          color: textColor,
                          fontFamily: bodyFont,
                        }}
                        data-testid={`rsvp-diet-${opt.toLowerCase()}`}
                      >
                        <div
                          className="w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0"
                          style={{ borderColor: form.dietary_preferences.includes(opt) ? accentColor : `${textColor}30` }}
                        >
                          {form.dietary_preferences.includes(opt) && (
                            <Check className="w-3 h-3" style={{ color: accentColor }} />
                          )}
                        </div>
                        {opt}
                      </button>
                    ))}
                  </div>
                  <textarea
                    value={form.allergies}
                    onChange={e => setForm(prev => ({ ...prev, allergies: e.target.value }))}
                    placeholder={labels.dietaryPlaceholder || 'Allergies or other requirements...'}
                    rows={2}
                    className="w-full px-3 py-2 rounded-lg border text-xs outline-none resize-none"
                    style={{ borderColor: `${textColor}15`, color: textColor, background: 'transparent', fontFamily: bodyFont }}
                    data-testid="rsvp-allergies"
                  />
                </div>
              )}

              {/* Children — only if attending */}
              {form.attending && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Info className="w-3.5 h-3.5" style={{ color: accentColor }} />
                    <label className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: textColor, fontFamily: eyebrowFont }}>
                      {labels.childrenLabel || 'Children'}
                    </label>
                  </div>
                  <p className="text-sm mb-2" style={{ color: secondaryColor, fontFamily: bodyFont }}>
                    {labels.childrenQuestion || 'Will you be bringing children?'}
                  </p>
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => setForm(prev => ({ ...prev, children_coming: true }))}
                      className="w-full py-3 rounded-xl border-2 text-sm font-medium transition-all"
                      style={form.children_coming === true ? {
                        borderColor: buttonBg,
                        backgroundColor: buttonBg,
                        color: buttonText,
                        fontFamily: bodyFont,
                      } : {
                        borderColor: `${textColor}20`,
                        color: textColor,
                        background: 'transparent',
                        fontFamily: bodyFont,
                      }}
                      data-testid="rsvp-children-yes"
                    >
                      {labels.childrenYes || 'Yes'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm(prev => ({ ...prev, children_coming: false, children_count: 0 }))}
                      className="w-full py-3 rounded-xl border-2 text-sm font-medium transition-all"
                      style={form.children_coming === false ? {
                        borderColor: `${textColor}40`,
                        backgroundColor: `${textColor}10`,
                        color: textColor,
                        fontFamily: bodyFont,
                      } : {
                        borderColor: `${textColor}20`,
                        color: textColor,
                        background: 'transparent',
                        fontFamily: bodyFont,
                      }}
                      data-testid="rsvp-children-no"
                    >
                      {labels.childrenNo || 'No'}
                    </button>
                  </div>

                  {/* Children count */}
                  {form.children_coming && (
                    <div className="mt-3">
                      <label className="block text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: textColor, fontFamily: eyebrowFont }}>
                        {labels.childrenCountLabel || 'How many children?'}
                      </label>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => setForm(prev => ({ ...prev, children_count: Math.max(0, prev.children_count - 1) }))}
                          className="w-10 h-10 rounded-full border-2 flex items-center justify-center"
                          style={{ borderColor: `${textColor}20`, color: textColor }}
                          data-testid="rsvp-children-minus"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="text-xl font-semibold w-10 text-center" style={{ color: textColor, fontFamily: headingFont }}>
                          {form.children_count}
                        </span>
                        <button
                          type="button"
                          onClick={() => setForm(prev => ({ ...prev, children_count: Math.min(10, prev.children_count + 1) }))}
                          className="w-10 h-10 rounded-full border-2 flex items-center justify-center"
                          style={{ borderColor: `${textColor}20`, color: textColor }}
                          data-testid="rsvp-children-plus"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Message */}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: textColor, fontFamily: eyebrowFont }}>
                  {labels.messageLabel || 'Message for the couple (Optional)'}
                </label>
                <textarea
                  value={form.message}
                  onChange={e => setForm(prev => ({ ...prev, message: e.target.value }))}
                  placeholder={labels.messagePlaceholder || 'Write us a few words...'}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border text-sm outline-none resize-none"
                  style={{ borderColor: `${textColor}20`, color: textColor, background: 'transparent', fontFamily: bodyFont }}
                  data-testid="rsvp-message"
                />
              </div>
              </div>{/* form fields */}

            {/* Error */}
            {error && (
              <p className="text-sm text-red-500 text-center" role="alert" data-testid="rsvp-error">{error}</p>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={form.attending === null || !form.name.trim() || submitting}
              className="w-full py-4 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-all disabled:opacity-50 rsvp-submit-shadow"
              style={{ backgroundColor: buttonBg, color: buttonText, fontFamily: bodyFont, ...getRsvpButtonShadowStyle(cs.buttonShadowStrength) }}
              data-testid="rsvp-submit-btn"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</>
              ) : (
                <><Send className="w-4 h-4" /> {labels.submitLabel || 'Confirm'}</>
              )}
            </button>
          </form>
        </AnimatedSection>
    </>);

  return renderTree(renderFormBody());
}
